import { useCallback } from 'react';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';
import type { RootState, AppDispatch } from '../store';
import {
  addNotification,
  removeNotification,
  clearNotifications,
  selectNotifications,
  selectVisibleNotifications,
  selectNotificationSettings,
} from '../slices/notificationSlice';
import type {
  Notification,
  NotificationInput,
  NotificationSettings,
  NotificationType,
} from '../types/notification.types';

/**
 * Typed dispatch hook for the store.
 * Defined locally to avoid circular dependency with @loci/hooks.
 */
const useAppDispatch: () => AppDispatch = useDispatch;

/**
 * Typed selector hook for the store.
 * Defined locally to avoid circular dependency with @loci/hooks.
 */
const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

/**
 * Return type for the useNotification hook.
 */
export interface UseNotificationReturn {
  /** All notifications in the queue (sorted by priority) */
  notifications: Notification[];
  /** Notifications currently visible based on maxVisible setting */
  visibleNotifications: Notification[];
  /** Current notification display settings */
  settings: NotificationSettings;
  /** Add a new notification to the queue */
  showNotification: (input: NotificationInput) => void;
  /** Remove a notification by ID */
  dismissNotification: (id: string) => void;
  /** Clear all notifications or filter by type */
  clearAll: (type?: NotificationType) => void;
}

/**
 * Convenience hook for managing notifications.
 * Provides access to notification state and dispatch actions.
 *
 * @example
 * ```tsx
 * const { showNotification, dismissNotification, visibleNotifications } = useNotification();
 *
 * // Show a success notification
 * showNotification({
 *   type: 'success',
 *   title: 'Event saved!',
 *   message: 'Your event has been saved successfully.',
 * });
 *
 * // Show a critical error
 * showNotification({
 *   type: 'error',
 *   title: 'Payment failed',
 *   priority: 'critical',
 *   dismissible: false,
 * });
 *
 * // Dismiss a notification
 * dismissNotification(notificationId);
 *
 * // Clear all error notifications
 * clearAll('error');
 * ```
 */
export function useNotification(): UseNotificationReturn {
  const dispatch = useAppDispatch();
  const notifications = useAppSelector(selectNotifications);
  const visibleNotifications = useAppSelector(selectVisibleNotifications);
  const settings = useAppSelector(selectNotificationSettings);

  const showNotification = useCallback(
    (input: NotificationInput) => {
      dispatch(addNotification(input));
    },
    [dispatch],
  );

  const dismissNotification = useCallback(
    (id: string) => {
      dispatch(removeNotification(id));
    },
    [dispatch],
  );

  const clearAll = useCallback(
    (type?: NotificationType) => {
      dispatch(clearNotifications(type));
    },
    [dispatch],
  );

  return {
    notifications,
    visibleNotifications,
    settings,
    showNotification,
    dismissNotification,
    clearAll,
  };
}
